<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useCharterCrewBriefingDetailStore } from '@/charter/stores/charter-crew-briefing-detail-store.ts';
import { useCharterCrewBriefingTemplatesStore } from '@/charter/stores/charter-crew-briefing-templates-store.ts';
import {
  getCrewBriefingTemplateFileNames,
  getLatestCrewBriefingTemplateFileName,
} from '@/charter/crew-briefing-template-selection.ts';
import { getApiErrorMessages } from '@/util/error-utils.ts';

const props = defineProps<{
  visible: boolean;
  charterId: number;
}>();

const emit = defineEmits<{
  'update:visible': [visible: boolean];
}>();

const detailStore = useCharterCrewBriefingDetailStore();
const templatesStore = useCharterCrewBriefingTemplatesStore();
const selectedTemplateFileName = ref<string | null>(null);
const templateLoadError = ref<string | null>(null);
const downloadError = ref<string | null>(null);

const dialogVisible = computed({
  get: () => props.visible,
  set: (visible: boolean) => {
    if (!detailStore.isDownloading) {
      emit('update:visible', visible);
    }
  },
});

const templateFileNames = computed(() =>
  getCrewBriefingTemplateFileNames(templatesStore.templates)
);

const canDownload = computed(
  () =>
    selectedTemplateFileName.value !== null &&
    !templatesStore.loading &&
    templateLoadError.value === null &&
    !detailStore.isDownloading
);

function reset(): void {
  selectedTemplateFileName.value = null;
  templateLoadError.value = null;
  downloadError.value = null;
}

function close(): void {
  if (!detailStore.isDownloading) {
    dialogVisible.value = false;
  }
}

async function loadTemplates(): Promise<void> {
  templateLoadError.value = null;

  try {
    await templatesStore.fetchTemplates();
    if (props.visible) {
      selectedTemplateFileName.value = getLatestCrewBriefingTemplateFileName(
        templatesStore.templates
      );
    }
  } catch (error) {
    templateLoadError.value = getApiErrorMessages(
      error,
      'Crew briefing templates could not be loaded.'
    ).join(' ');
  }
}

async function download(): Promise<void> {
  const templateFileName = selectedTemplateFileName.value;
  if (!templateFileName || !canDownload.value) {
    return;
  }

  downloadError.value = null;

  try {
    await detailStore.downloadPdf(props.charterId, templateFileName);
    dialogVisible.value = false;
  } catch (error) {
    downloadError.value = getApiErrorMessages(
      error,
      'Crew briefing PDF could not be downloaded.'
    ).join(' ');
  }
}

watch(
  () => props.visible,
  visible => {
    if (visible) {
      reset();
      void loadTemplates();
    } else if (!detailStore.isDownloading) {
      reset();
    }
  }
);
</script>

<template>
  <p-dialog
    v-model:visible="dialogVisible"
    modal
    header="Download crew briefing"
    :closable="!detailStore.isDownloading"
    :closeOnEscape="!detailStore.isDownloading"
    :style="{ width: '20rem', maxWidth: '95vw' }"
    @hide="reset"
  >
    <p class="mt-0 text-sm text-slate-600">
      Select the DOCX template to use for the generated PDF.
    </p>

    <div
      v-if="templatesStore.loading"
      class="flex items-center justify-center gap-3 py-6 text-slate-500"
    >
      <p-progress-spinner class="h-8 w-8" strokeWidth="5" />
      <span>Loading templates...</span>
    </div>

    <div v-else-if="templateLoadError" class="space-y-3">
      <p-message severity="error" :closable="false">
        {{ templateLoadError }}
      </p-message>
      <p-button
        label="Retry"
        icon="pi pi-refresh"
        severity="danger"
        outlined
        :disabled="detailStore.isDownloading"
        @click="loadTemplates"
      />
    </div>

    <p-message
      v-else-if="templateFileNames.length === 0"
      severity="info"
      :closable="false"
    >
      No crew briefing templates are available.
    </p-message>

    <div v-else class="flex flex-col gap-2">
      <label for="crew-briefing-template" class="font-medium text-slate-700">Template</label>
      <p-dropdown
        inputId="crew-briefing-template"
        v-model="selectedTemplateFileName"
        :options="templateFileNames"
        placeholder="Select a template"
        filter
        class="w-full"
        :disabled="detailStore.isDownloading"
      />
    </div>

    <p-message v-if="downloadError" severity="error" :closable="false" class="mt-3">
      {{ downloadError }}
    </p-message>

    <template #footer>
      <p-button
        label="Cancel"
        severity="secondary"
        outlined
        :disabled="detailStore.isDownloading"
        @click="close"
      />
      <p-button
        label="Download"
        icon="pi pi-download"
        :disabled="!canDownload"
        :loading="detailStore.isDownloading"
        @click="download"
      />
    </template>
  </p-dialog>
</template>

<style scoped lang="scss">
.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}
</style>
